import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useDescope } from '@descope/react-sdk';
import { Dispatch } from 'Rdx/store';
import { initializeApi } from 'Src/Api';
import { TenantDto } from '@ampsec/platform-client';
import { HOME_ROUTE, TENANTS_ROUTE, SIGN_IN, POLICIES_ROUTE } from 'Core-utils/routes';
import { useHash, useQuery } from 'Hooks/index';
import { incrementLoginMetric } from 'Core-utils/helpers/SentryHelpers';
import {
    DESCOPE_AUTH_ERROR_MESSAGE,
    exchangeDescopeTokenWithRefresh,
    resolveDescopeSessionToken,
} from 'Core-utils/helpers/descopeAuth';
import { getProfile } from 'Apis/library';
import { DescopeSignIn } from '../SignIn/DescopeSignIn';
import _ from 'lodash';

// Hidden container for the SP-initiated resume widget — the SignIn page's preload
// styles are scoped to that page, so we hide it inline here instead.
const HIDDEN_WIDGET_STYLE: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    opacity: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
};

export const AuthRedirectPage = () => {
    const hash = useHash();
    const query = useQuery();
    const navigate = useNavigate();
    const dispatch = useDispatch<Dispatch>();
    const sdk = useDescope();

    const isDescopeEnabled = ['true', '1', 'yes'].includes(
        (process.env.ENABLE_DESCOPE_AUTH || process.env.REACT_APP_ENABLE_DESCOPE_AUTH || '').toLowerCase(),
    );
    const descopeProjectId = process.env.DESCOPE_PROJECT_ID || process.env.REACT_APP_DESCOPE_PROJECT_ID || '';
    const descopeFlowId = process.env.DESCOPE_FLOW_ID || process.env.REACT_APP_DESCOPE_FLOW_ID || 'sign-up-or-in';
    const descopeConfigured = isDescopeEnabled && Boolean(descopeProjectId);

    // Google/Entra (and our own post-exchange bounce) arrive with the platform token
    // in the URL. Descope SSO arrives with a `code`:
    //   - SP-initiated also carries `descope-login-flow` (a flow continuation the
    //     widget resumes via the flow endpoint).
    //   - IdP-initiated (e.g. Okta tile) is a bare `code` to exchange directly.
    const urlToken = hash.get('token') || query.get('token');
    const ssoCode = query.get('code');
    const isFlowResume = descopeConfigured && !urlToken && Boolean(ssoCode) && query.has('descope-login-flow');
    const isIdpInitiated = descopeConfigured && !urlToken && Boolean(ssoCode) && !query.has('descope-login-flow');

    const completeLogin = useCallback(
        async (token: string) => {
            dispatch.auth.setAuthToken(token);

            const [meRes, tenantsRes] = await Promise.all([
                getProfile(token),
                fetch(`${process.env.PLATFORM_API_URL}/api/v1/tenants`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }),
            ]);
            if (meRes.ok && tenantsRes.ok) {
                const me = await meRes.json();
                const tenantPage = await tenantsRes.json();
                const tenants = tenantPage.data;
                dispatch.auth.setUser(me);
                dispatch.auth.setTenants(
                    tenants?.map((tenant: TenantDto) => ({ id: tenant.id, displayValue: tenant.displayValue })),
                );
                if (tenants) {
                    if (tenants?.length === 1) {
                        const tenant = tenants[0];
                        dispatch.auth.setSelectedTenants(tenants);
                        incrementLoginMetric(tenant.displayValue, tenant.id, me.email!);
                        const installedConnectorsRes = await fetch(
                            `${process.env.PLATFORM_API_URL}/api/v1/connectors?limit=1`,
                            {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            },
                        );
                        if (installedConnectorsRes.ok) {
                            const responseJson = await installedConnectorsRes.json();
                            _.isEmpty(responseJson?.data) ? navigate(POLICIES_ROUTE) : navigate(HOME_ROUTE);
                        }
                    } else {
                        navigate(TENANTS_ROUTE);
                    }
                }
                initializeApi(token);
            } else {
                localStorage.clear();
                navigate(SIGN_IN);
            }
        },
        [dispatch, navigate],
    );

    const failToSignIn = useCallback(
        (message?: string) => {
            dispatch.ssoModel.setSsoOption('Descope');
            dispatch.ssoModel.setError(message || DESCOPE_AUTH_ERROR_MESSAGE);
            navigate(SIGN_IN);
        },
        [dispatch, navigate],
    );

    const exchangeAmpAndContinue = useCallback(
        async (descopeToken: string) => {
            const ampToken = await exchangeDescopeTokenWithRefresh(descopeToken);
            await completeLogin(ampToken);
        },
        [completeLogin],
    );

    // IdP-initiated: exchange the bare SSO code for a Descope session, then for our
    // platform token. The protocol isn't encoded in the callback, so try SAML first
    // (most common) and fall back to OIDC — the two hit different Descope endpoints,
    // so a wrong-protocol attempt is rejected without consuming the code.
    const completeIdpInitiated = useCallback(
        async (code: string) => {
            try {
                dispatch.ssoModel.setSsoOption('Descope');
                let response = await sdk.saml.exchange(code);
                if (!response.ok) {
                    response = await sdk.oauth.exchange(code);
                }
                const sessionJwt = response.ok ? response.data?.sessionJwt : undefined;
                if (!sessionJwt) {
                    failToSignIn();
                    return;
                }
                await exchangeAmpAndContinue(sessionJwt);
            } catch (error) {
                failToSignIn(error instanceof Error ? error.message : undefined);
            }
        },
        [sdk, exchangeAmpAndContinue, failToSignIn, dispatch],
    );

    // SP-initiated: the widget resumes the flow and hands us the session token.
    const handleFlowResumed = useCallback(
        async (descopeTokenFromEvent: string | null) => {
            try {
                dispatch.ssoModel.setSsoOption('Descope');
                const descopeToken = await resolveDescopeSessionToken(descopeTokenFromEvent);
                if (!descopeToken) {
                    failToSignIn();
                    return;
                }
                await exchangeAmpAndContinue(descopeToken);
            } catch (error) {
                failToSignIn(error instanceof Error ? error.message : undefined);
            }
        },
        [exchangeAmpAndContinue, failToSignIn, dispatch],
    );

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.has('error')) {
            dispatch.ssoModel.setError(queryParams.get('error'));
            navigate(SIGN_IN);
            return;
        }
        if (urlToken) {
            completeLogin(urlToken);
            return;
        }
        if (isIdpInitiated && ssoCode) {
            completeIdpInitiated(ssoCode);
            return;
        }
        // SP-initiated resume is driven by the hidden widget's onAuthenticated below.
        if (!isFlowResume) {
            navigate(SIGN_IN);
        }
    }, []);

    if (isFlowResume) {
        return (
            <div style={HIDDEN_WIDGET_STYLE} aria-hidden>
                <DescopeSignIn
                    flowId={descopeFlowId}
                    onAuthenticated={handleFlowResumed}
                    onError={failToSignIn}
                    isVisible={false}
                />
            </div>
        );
    }

    return <></>;
};

export default AuthRedirectPage;
